import { createApp } from "vue/dist/vue.esm-bundler.js";
import KdProduct from "../components/KdProduct.vue";
import KdIcon from "../components/KdIcon.vue";
import KdIconCartPlus from "../components/Icon/KdCartPlus.vue";
import KdIconCheckmark from "../components/Icon/KdCheckmark.vue";
import KdIconHeart from "../components/Icon/KdHeart.vue";
import KdIconPlus from "../components/Icon/KdPlus.vue";

class ProductFilterService {
    constructor() {
        this.priceFilter = null;
        this.orderby = null;
        this.isLoading = false;
        this._activeRequestId = 0;
        this._abortController = null;

        if (document.readyState === "loading") {
            document.addEventListener("DOMContentLoaded", () => this.init());
        } else {
            this.init();
        }
    }

    init() {
        const form = document.querySelector(".plw-filters");
        const listContainer = document.querySelector("#kd-products-list");

        if (!form || !listContainer) return;

        this.form = form;
        this.listContainer = listContainer;

        this.syncStateFromUrl();
        this.mountProducts();
        this.bindFilterChangeEvents();
        this.bindFormSubmit();
        this.bindPaginationIntercept();
        this.bindBrowserNavigation();
    }

    // 1) URL -> JS STATE
    syncStateFromUrl() {
        const params = new URL(location.href).searchParams;

        this.orderby = params.get("orderby");
        this.priceFilter = {
            min_price: params.has("min_price") ? +params.get("min_price") : null,
            max_price: params.has("max_price") ? +params.get("max_price") : null,
        };
    }

    // 2) EVENTS
    bindFilterChangeEvents() {
        document.addEventListener("kd:filters-changed", () => {
            this.applyFiltersNow();
        });
    }

    bindFormSubmit() {
        this.form.addEventListener("submit", (event) => {
            event.preventDefault();
            this.applyFiltersNow();
        });
    }

    bindPaginationIntercept() {
        this.listContainer.addEventListener("click", (event) => {
            const link = event.target.closest(".woocommerce-pagination a.page-numbers");
            if (!link) return;

            event.preventDefault();

            const url = new URL(link.href, window.location.origin).toString();
            window.history.pushState({ kdProductsFilter: true }, "", url);

            this.syncStateFromUrl();
            this.updateProducts(url);
        });
    }

    bindBrowserNavigation() {
        window.addEventListener("popstate", () => {
            if (!this.listContainer) return;

            this.syncStateFromUrl();
            this.updateProducts(window.location.href);
        });
    }

    // 3) APPLY FILTERS (FORM -> URL -> FETCH)
    applyFiltersNow() {
        const newUrl = this.buildUrlWithFilters();
        this.updateProducts(newUrl);
    }

    buildUrlWithFilters() {
        const url = this.createBaseUrl();
        const params = url.searchParams;
        const allTaxKeys = this.getAllTaxKeysFromForm();
        const activeFilters = this.collectFormFilters();

        this.removeExistingFilterParams(params, allTaxKeys);
        this.applyFilterParams(params, activeFilters);
        this.applyOrderbyFromForm(params);
        this.applyPriceFromForm(params);

        url.search = params.toString();
        window.history.pushState({ kdProductsFilter: true }, "", url.toString());

        return url.toString();
    }

    createBaseUrl() {
        const currentUrl = new URL(window.location.href);
        const url = new URL(currentUrl.href);

        url.pathname = url.pathname.replace(/\/page\/\d+\/?$/, "/");

        return url;
    }

    // 4) READ FORM (TAX FILTERS)
    collectFormFilters() {
        const filters = {};

        const pushValue = (tax, value) => {
            if (!tax || !value) return;
            filters[tax] = filters[tax] || [];
            filters[tax].push(value);
        };

        this.form.querySelectorAll('input[type="checkbox"][data-tax]').forEach((input) => {
            if (input.checked) {
                pushValue(input.dataset.tax, input.value);
            }
        });

        return filters;
    }

    applyFilterParams(params, activeFilters) {
        Object.entries(activeFilters).forEach(([tax, values]) => {
            if (!values?.length) return;

            [...new Set(values)].forEach((value) => {
                if (tax === "stock_level") {
                    params.set("stock_level", value);
                } else {
                    params.append(`${tax}[]`, value);
                }
            });
        });
    }

    getAllTaxKeysFromForm() {
        const keys = new Set();

        this.form.querySelectorAll("[data-tax]").forEach((el) => {
            const tax = el.dataset?.tax;
            if (tax) keys.add(tax);
        });

        return [...keys];
    }

    removeExistingFilterParams(params, taxKeys) {
        taxKeys.forEach((tax) => {
            [...params.keys()].forEach((key) => {
                if (key === tax || key === `${tax}[]` || key.startsWith(`${tax}[`)) {
                    params.delete(key);
                }
            });
        });

        ["min_price", "max_price", "orderby", "stock_level", "paged", "page", "product-page"].forEach((k) => params.delete(k));
    }

    // 5) READ FORM (ORDERBY + PRICE)
    applyOrderbyFromForm(params) {
        const orderbyEl = this.form.querySelector('[name="orderby"]');
        const orderby = orderbyEl ? String(orderbyEl.value || "").trim() : "";

        if (!orderby || orderby === "menu_order") return;
        params.set("orderby", orderby);
    }

    applyPriceFromForm(params) {
        const minEl = this.form.querySelector('[name="min_price"]');
        const maxEl = this.form.querySelector('[name="max_price"]');

        const minVal = minEl ? minEl.value : "";
        const maxVal = maxEl ? maxEl.value : "";

        if (minVal !== "") params.set("min_price", minVal);
        if (maxVal !== "") params.set("max_price", maxVal);
    }

    // 6) UI LOADING
    setLoading(isLoading) {
        this.isLoading = !!isLoading;

        const overlay = document.getElementById("kd-products-loading");
        if (!overlay) return;

        overlay.classList.toggle("opacity-100", this.isLoading);
        overlay.classList.toggle("opacity-0", !this.isLoading);
        overlay.classList.toggle("pointer-events-none", !this.isLoading);
    }

    // 7) FETCH + REPLACE LIST
    async updateProducts(url) {
        this.setLoading(true);

        const requestId = ++this._activeRequestId;

        if (this._abortController) {
            this._abortController.abort();
        }

        this._abortController = new AbortController();
        const { signal } = this._abortController;

        try {
            const response = await fetch(url, {
                headers: { "X-Requested-With": "XMLHttpRequest" },
                credentials: "same-origin",
                signal,
            });

            if (!response.ok) return;

            const html = await response.text();

            if (requestId !== this._activeRequestId) return;

            const doc = new DOMParser().parseFromString(html, "text/html");
            const newList = doc.querySelector("#kd-products-list");
            if (!newList) return;

            this.listContainer.querySelectorAll("kd-product").forEach((el) => {
                if (el.__kdVueApp__) {
                    el.__kdVueApp__.unmount();
                    el.__kdVueApp__ = null;
                }
            });

            this.listContainer.innerHTML = newList.innerHTML;
            this.mountProducts();
        } catch (e) {
            if (e?.name === "AbortError") return;
            throw e;
        } finally {
            if (requestId === this._activeRequestId) {
                this.setLoading(false);
            }
        }
    }

    // 8) MOUNT PRODUCTS
    mountProducts() {
        mountKdProducts(this.listContainer);
    }
}

export default ProductFilterService;

// HELPERS (PRODUCT MOUNTING)
function safeParseProduct(raw) {
    if (!raw) return {};
    try {
        return JSON.parse(raw) || {};
    } catch {
        return {};
    }
}

function mountKdProducts(root = document) {
    root.querySelectorAll("kd-product").forEach((el) => {
        if (el.__kdVueApp__) return;

        const productData = safeParseProduct(
            el.getAttribute(":product-data") || el.dataset.product || ""
        );

        const variant = el.dataset.variant || "small";

        const app = createApp(KdProduct, { productData, variant });

        app.component("kd-icon", KdIcon);
        app.component("kd-icon-heart", KdIconHeart);
        app.component("kd-icon-cart-plus", KdIconCartPlus);
        app.component("kd-icon-plus", KdIconPlus);
        app.component("kd-icon-checkmark", KdIconCheckmark);

        app.mount(el);
        el.__kdVueApp__ = app;
    });
}
